import React, { FC, useEffect, useState } from 'react';
import Modal from '@common/modal';
import {
  matchingAgreeCheckKeys,
  useMatchTermsAndCheckAgreeProfile,
  useSaveMatchTerms,
} from '@repository/terms/useTerms';
import { keys as profileBasicKeys } from '@repository/profiles/useProfileBasic';
import { getAccessToken } from '@repository/AccessToken';
import TermList from '@components/profile/modals/terms/termList';
import { queryClient } from '@utils/queryClient';
import useDebounce from '@utils/hooks/useDebounce';

import classnames from 'classnames/bind';
import styles from './terms.module.scss';

const cx = classnames.bind(styles);

const getInitialState = (status = false, length = 3) => {
  return Array.from({ length }, () => status);
};

interface ITermsModalProps {
  visible: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const TermsModal: FC<ITermsModalProps> = ({ visible, onConfirm, onClose }) => {
  const isLogin = !!getAccessToken();

  const { data: termsData } = useMatchTermsAndCheckAgreeProfile(isLogin && visible);
  const { mutateAsync: saveMatchTermsMutateAsync } = useSaveMatchTerms(async () => {
    //프로필 view 쪽 재조회
    await queryClient.invalidateQueries([...profileBasicKeys, 'view']);

    //프로필 관련 정보드 재조회
    await queryClient.refetchQueries(['profile'], { active: true });
    //동의 여부 데이터 재조회
    await queryClient.refetchQueries(matchingAgreeCheckKeys, { active: true, exact: true });
  });

  const [checkStatus, setCheckStatus] = useState(getInitialState());

  const isNotAgreeRequiredTerms = termsData?.some((item, idx) => {
    if (item.isRequired) {
      return !checkStatus[idx];
    }

    return false;
  });

  const onSubmit = useDebounce(async () => {
    if (isNotAgreeRequiredTerms) return;
    if (!termsData?.length) return;

    const agreeTermsSns = checkStatus
      .map((agreeYn, idx) => {
        if (agreeYn && termsData[idx]?.sn) {
          return termsData[idx].sn;
        }

        return null;
      })
      .filter((item) => item) as number[];

    await saveMatchTermsMutateAsync(agreeTermsSns);

    onConfirm();
  }, 300);

  const onCheck = ({ idx }: { idx: number }) => {
    setCheckStatus((prevCheckStatus) => {
      const postCheckStatus = prevCheckStatus;
      postCheckStatus[idx] = !prevCheckStatus[idx];
      return [...postCheckStatus];
    });
  };

  useEffect(() => {
    if (termsData) {
      setCheckStatus(getInitialState(false, termsData.length));
    }
  }, [termsData]);

  if (!termsData) return null;

  return (
    <Modal visible={visible} dimmed={true}>
      <div className={cx('layerTerms')}>
        <strong className={cx('title')}>
          서비스 이용을 위해
          <br />
          아래의 항목에 동의해주세요.
        </strong>

        <div className={cx('termsArea')}>
          <TermList termList={termsData} checkStatus={checkStatus} onCheck={onCheck} />
        </div>

        <button className={cx('btnAgreement')} type="button" onClick={onSubmit} disabled={isNotAgreeRequiredTerms}>
          동의하고 시작하기
        </button>

        <a className={cx('btnClose')} role="button" onClick={onClose}>
          <span className={cx('hidden')}>close</span>
        </a>
      </div>
    </Modal>
  );
};

export default TermsModal;
